import { ENV, WebSocketMessage } from '@qse/constants'
import { useWebSocketStore } from '@qse/stores'

import type { MessageHandler, RealtimeConfig } from './types'

// Global singleton instance
let globalWebSocketManager: WebSocketManager | null = null

/**
 * WebSocket Manager class for handling real-time connections
 */
export class WebSocketManager {
  private config: RealtimeConfig
  private messageHandler: MessageHandler
  private reconnectTimeoutId: number | null = null

  constructor(config: RealtimeConfig, messageHandler: MessageHandler) {
    this.config = config
    this.messageHandler = messageHandler
  }

  /**
   * Update configuration and message handler
   */
  updateConfig(config: RealtimeConfig, messageHandler: MessageHandler) {
    this.config = config
    this.messageHandler = messageHandler
  }

  /**
   * Get the WebSocket URL with authentication token
   */
  private async getWebSocketUrl(): Promise<string> {
    try {
      const token = await this.config.getToken()

      if (!token) {
        throw new Error('No authentication token available')
      }

      const baseUrl = this.config.websocketUrl || ENV.WEBSOCKET_URL
      const separator = baseUrl.includes('?') ? '&' : '?'

      return `${baseUrl}${separator}authorization=${encodeURIComponent(token)}`
    } catch (error) {
      console.warn('Failed to get WebSocket URL:', error)
      throw new Error('Authentication required for WebSocket connection')
    }
  }

  /**
   * Create and configure WebSocket connection
   */
  private createWebSocket(url: string): WebSocket {
    const socket = new WebSocket(url)

    socket.onopen = this.handleOpen.bind(this)
    socket.onmessage = this.handleMessage.bind(this)
    socket.onerror = this.handleError.bind(this)
    socket.onclose = this.handleClose.bind(this)

    return socket
  }

  /**
   * Handle WebSocket open event
   */
  private handleOpen(event: Event): void {
    console.log('WebSocket connected:', event)

    const { setConnected, setConnecting, setError, setConnectionId } = useWebSocketStore.getState()

    setConnected(true)
    setConnecting(false)
    setError(null)

    // Generate connection ID
    const connectionId = `frontend-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

    setConnectionId(connectionId)
  }

  /**
   * Handle WebSocket message event
   */
  private handleMessage(event: MessageEvent): void {
    try {
      const message: WebSocketMessage = JSON.parse(event.data)

      this.messageHandler(message)
    } catch (error) {
      console.error('Error parsing WebSocket message:', error)
    }
  }

  /**
   * Handle WebSocket error event
   */
  private handleError(error: Event): void {
    console.error('WebSocket error:', error)

    const { setConnected, setConnecting, setError } = useWebSocketStore.getState()

    setConnected(false)
    setConnecting(false)
    setError('WebSocket connection error')
  }

  /**
   * Handle WebSocket close event
   */
  private handleClose(event: CloseEvent): void {
    console.log('WebSocket disconnected:', event)

    const { setConnected, setConnecting, setReconnectTimeout } = useWebSocketStore.getState()

    setConnected(false)
    setConnecting(false)

    // Don't reconnect for authentication errors (401) or intentional closes
    if (event.code === 1000 || event.code === 4001) {
      console.log('WebSocket closed intentionally or due to authentication error, not reconnecting')

      return
    }

    // Attempt to reconnect if not closed intentionally and reconnection is enabled
    if (this.config.enableReconnection !== false) {
      const delay = this.config.reconnectDelay || 5000

      this.reconnectTimeoutId = window.setTimeout(() => {
        console.log('🔄 Attempting to reconnect WebSocket...')
        this.connect()
      }, delay)

      setReconnectTimeout(this.reconnectTimeoutId)
    }
  }

  /**
   * Connect to WebSocket
   */
  async connect(): Promise<void> {
    const { socket, isConnecting, setConnecting, setConnected, setError, setSocket } =
      useWebSocketStore.getState()

    // Don't connect if already connected or connecting
    if (socket?.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected, skipping connection attempt')

      return
    }

    if (isConnecting) {
      console.log('WebSocket connection already in progress, skipping connection attempt')

      return
    }

    // Don't connect if socket is in connecting state
    if (socket?.readyState === WebSocket.CONNECTING) {
      console.log('WebSocket is currently connecting, skipping connection attempt')

      return
    }

    console.log('🔌 Starting WebSocket connection...')
    setConnecting(true)
    setError(null)

    try {
      console.log('🔌 Attempting to establish WebSocket connection...')
      const url = await this.getWebSocketUrl()
      const newSocket = this.createWebSocket(url)

      setSocket(newSocket)
      console.log('✅ WebSocket connection initiated')
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error)

      setConnected(false)
      setConnecting(false)

      // Set appropriate error message based on error type
      if (error instanceof Error && error.message.includes('Authentication')) {
        setError('Authentication required for WebSocket connection')
      } else {
        setError('Failed to create WebSocket connection')
      }
    }
  }

  /**
   * Disconnect from WebSocket
   */
  async disconnect(): Promise<void> {
    const { socket, reconnectTimeout, setSocket, setReconnectTimeout, reset } =
      useWebSocketStore.getState()

    // Clear reconnection timeout
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout)
      setReconnectTimeout(null)
    }

    // Clear our internal timeout
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId)
      this.reconnectTimeoutId = null
    }

    // Close socket if exists
    if (socket) {
      socket.close(1000) // Close intentionally
      setSocket(null)
    }

    reset()
  }

  /**
   * Get current connection state
   */
  getConnectionState() {
    const { isConnected, isConnecting, error, connectionId } = useWebSocketStore.getState()

    return {
      isConnected,
      isConnecting,
      error,
      connectionId,
    }
  }
}

/**
 * Get or create global WebSocket manager instance
 */
export function getGlobalWebSocketManager(
  config: RealtimeConfig,
  messageHandler: MessageHandler,
): WebSocketManager {
  if (!globalWebSocketManager) {
    globalWebSocketManager = new WebSocketManager(config, messageHandler)
  } else {
    // Update existing manager with new config and handler
    globalWebSocketManager.updateConfig(config, messageHandler)
  }

  return globalWebSocketManager
}

/**
 * Destroy global WebSocket manager instance
 */
export function destroyGlobalWebSocketManager(): void {
  if (globalWebSocketManager) {
    globalWebSocketManager.disconnect()
    globalWebSocketManager = null
  }
}
